import { computed, observable } from 'mobx'

import {
  IOperationRule,
  IOperationRuleInput,
  LocationType,
  OperationSubjectType,
  OperationType,
} from '~/client/graph'
import { RoundBracket } from '~/client/src/shared/enums/Brackets'
import BaseModel from '~/client/src/shared/models/BaseModel'
import { AndOrOperator } from '~/client/src/shared/models/LogicOperation'
import { enumToList } from '~/client/src/shared/utils/converters'

import { TagType } from '../enums/TagType'

const SEPARATOR = ` ${AndOrOperator.OR} `
const FIELD_TAG_TYPE = 'field'
const USER_TAG_TYPE = 'user'
// Lowercase, unlike the condition's COMPANY_TAG_TYPE: Graph matches assignee tokens case-sensitively.
export const COMPANY_ASSIGNEE_TAG_TYPE = 'company'
export const COMPANY_TAG_TYPE = 'Company'
export const COMPANY_TYPE_TAG_TYPE = 'CompanyType'
const TAG_REGEX = /\[(\w+)\]\[([^\]]+)\]/g
const NOT_OPERATOR = '!'

const LOCATION_TYPES_SET = new Set<string>(enumToList(LocationType))

interface IParsedTagGroup {
  tagIds: Set<string>
  hasNotOperator: boolean
}

export default class OperationRule
  extends BaseModel<IOperationRule>
  implements IOperationRule
{
  public static NEW_ID = 'new-id'
  public static ASSIGNMENT_PLACEHOLDER = ''

  public static fromDto(dto: IOperationRule) {
    const rule = new OperationRule(
      dto.expression,
      dto.operation,
      dto.operationSubject,
      dto.projectId,
      dto.assignExpression,
      dto.id,
      dto.createdAt,
      dto.updatedAt,
    )
    rule.name = dto.name || null
    return rule
  }

  public static toDto(rule: OperationRule): IOperationRuleInput {
    return {
      id: rule.isExisting ? rule.id : null,
      projectId: rule.projectId,
      name: rule.name || null,
      expression: rule.expression,
      assignExpression: rule.assignExpression,
      operation: rule.operation,
      operationSubject: rule.operationSubject,
      createdAt: rule.createdAt,
      updatedAt: rule.updatedAt,
    }
  }

  public static makeTagExpressionString(
    tagType: TagType | string,
    tagId: string,
  ): string {
    return `[${tagType}][${tagId}]`
  }

  @observable public assignmentsAsSubexpressions: string[] = [
    OperationRule.ASSIGNMENT_PLACEHOLDER,
  ]

  /** Shown to people signing a one-sign-off-per-rule step, e.g. "Mechanical". */
  public name: string = null

  public constructor(
    public expression: string,
    public operation: OperationType,
    public operationSubject: OperationSubjectType,
    public projectId: string,
    public assignExpression: string = OperationRule.ASSIGNMENT_PLACEHOLDER,
    id: string = '',
    createdAt: number = 0,
    updatedAt: number = 0,
  ) {
    super(id)

    this.assignmentsAsSubexpressions = (
      assignExpression || OperationRule.ASSIGNMENT_PLACEHOLDER
    ).split(SEPARATOR)

    this.setCreatedAt(createdAt)
    this.setUpdatedAt(updatedAt)
  }

  public get isExisting(): boolean {
    return !!this.id && this.id !== OperationRule.NEW_ID
  }

  public setExpression(expression: string) {
    if (expression) {
      this.expression = expression
    }
  }

  public updateAssignmentExpressions() {
    this.assignExpression = this.assignmentsAsSubexpressions
      .filter(exp => !!exp)
      .join(SEPARATOR)
  }

  @computed
  public get assignedFieldIds(): string[] {
    return this.getAssignedIds(FIELD_TAG_TYPE)
  }

  @computed
  public get assignedUserIds(): string[] {
    return this.getAssignedIds(USER_TAG_TYPE)
  }

  /** Companies whose project members are all assignees of the rule. */
  @computed
  public get assignedCompanyIds(): string[] {
    return this.getAssignedIds(COMPANY_ASSIGNEE_TAG_TYPE)
  }

  private getAssignedIds(tagType: string): string[] {
    const ids: string[] = []
    let match: RegExpExecArray | null

    const regex = new RegExp(TAG_REGEX.source, 'g')
    while ((match = regex.exec(this.assignExpression)) !== null) {
      const [, matchedTagType, tagId] = match
      if (matchedTagType === tagType) {
        ids.push(tagId)
      }
    }

    return ids
  }

  public isFieldAssigned(fieldId: string): boolean {
    return this.assignedFieldIds.includes(fieldId)
  }

  @computed
  public get parsedLocationTags(): IParsedTagGroup {
    return this.parseTagGroupFromExpression(LOCATION_TYPES_SET, 0)
  }

  @computed
  public get parsedCompanyTags(): IParsedTagGroup {
    const firstGroup = this.parseTagGroupFromExpression(
      new Set<string>([COMPANY_TAG_TYPE]),
      0,
    )
    if (firstGroup.tagIds.size > 0) {
      return firstGroup
    }

    const rightBracketIndex = this.expression.indexOf(RoundBracket.RIGHT)
    if (rightBracketIndex === -1) {
      return { tagIds: new Set<string>(), hasNotOperator: false }
    }

    return this.parseTagGroupFromExpression(
      new Set<string>([COMPANY_TAG_TYPE]),
      rightBracketIndex + 1,
    )
  }

  /**
   * Matched on the id alone: `[Zone][id]` records the kind the location had when the rule
   * was written, and a re-kind leaves both the rule and the permit's SiteLocation snapshot
   * behind (STX1-597).
   */
  public isLocationTagValid(locationId: string): boolean {
    const { tagIds, hasNotOperator } = this.parsedLocationTags

    if (tagIds.size === 0) {
      return true
    }

    const isValid = tagIds.has(locationId)
    return hasNotOperator ? !isValid : isValid
  }

  @computed
  public get parsedCompanyTypeTags(): IParsedTagGroup {
    const firstGroup = this.parseTagGroupFromExpression(
      new Set<string>([COMPANY_TYPE_TAG_TYPE]),
      0,
    )
    if (firstGroup.tagIds.size > 0) {
      return firstGroup
    }

    const rightBracketIndex = this.expression.indexOf(RoundBracket.RIGHT)
    if (rightBracketIndex === -1) {
      return { tagIds: new Set<string>(), hasNotOperator: false }
    }

    return this.parseTagGroupFromExpression(
      new Set<string>([COMPANY_TYPE_TAG_TYPE]),
      rightBracketIndex + 1,
    )
  }

  public isCompanyTagValid(companyId: string): boolean {
    const { tagIds, hasNotOperator } = this.parsedCompanyTags

    if (tagIds.size === 0) {
      return true
    }

    const isValid = tagIds.has(companyId)
    return hasNotOperator ? !isValid : isValid
  }

  public isCompanyTypeTagValid(companyTypeId: string): boolean {
    const { tagIds, hasNotOperator } = this.parsedCompanyTypeTags

    if (tagIds.size === 0) {
      return true
    }

    const isValid = tagIds.has(companyTypeId)
    return hasNotOperator ? !isValid : isValid
  }

  private parseTagGroupFromExpression(
    validTagTypes: Set<string>,
    startOffset: number,
  ): IParsedTagGroup {
    const tagIds = new Set<string>()
    const expression = this.expression.slice(startOffset)

    const leftBracketIndex = expression.indexOf(RoundBracket.LEFT)
    const rightBracketIndex = expression.indexOf(RoundBracket.RIGHT)

    if (leftBracketIndex === -1 || rightBracketIndex === -1) {
      return { tagIds, hasNotOperator: false }
    }

    const notOperatorIndex = leftBracketIndex - 2
    const hasNotOperator =
      notOperatorIndex >= 0 && expression[notOperatorIndex] === NOT_OPERATOR

    const bracketContent = expression.slice(
      leftBracketIndex + 1,
      rightBracketIndex,
    )

    const regex = new RegExp(TAG_REGEX.source, 'g')
    let match: RegExpExecArray | null
    while ((match = regex.exec(bracketContent)) !== null) {
      const [, tagType, tagId] = match
      if (validTagTypes.has(tagType)) {
        // Location tags are matched on the id alone, see isLocationTagValid
        tagIds.add(tagId)
      }
    }

    return { tagIds, hasNotOperator }
  }
}
